import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "../../../assets/css/gcstudent/digitallibrarybooks/DigitalLibraryBooks.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import Swal from "sweetalert2";

const CATEGORIES = [
  "All",
  "Computer Science",
  "Artificial Intelligence",
  "Networking",
  "Software Engineering",
  "Electronics",
  "Mathematics",
  "Physics",
  "Other",
];

const PAGE_SIZE = 8;



/* ─────────────────────────────────────────────
   Book Card
───────────────────────────────────────────── */
const BookCard = ({ book, onClick, index }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { baseUrlForFiles } = useContext(AppContext);

  const imgSrc =
    book.img && !imgFailed
      ? `${baseUrlForFiles}/${book.img}`
      : null;

  return (
    <Col xs={12} sm={6} md={4} lg={3} className="gcstudent-bb-card-col">
      <div
        className="gcstudent-bb-card"
        onClick={() => onClick(book)}
        style={{ animationDelay: `${index * 0.07}s` }}
      >
        <div className="gcstudent-bb-card-img-wrap">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={book.title}
              className="gcstudent-bb-card-cover-img"
              onError={() => {
                // TEMP DEBUG: open devtools console to see exactly which
                // URL 404'd — remove this console.error once images work.
                console.error("Ebook cover image failed to load:", imgSrc, "raw book.img:", book.img);
                setImgFailed(true);
              }}
            />
          ) : (
            <div className="gcstudent-bb-card-placeholder">
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
              </svg>
              <span>PDF</span>
            </div>
          )}
          <div className="gcstudent-bb-card-overlay" />
        </div>

        <div className="gcstudent-bb-card-body">
          <span className="gcstudent-bb-cat-tag">{book.dept || "General"}</span>
          <h6 className="gcstudent-bb-card-title">{book.title}</h6>
          <p className="gcstudent-bb-card-author">{book.author}</p>
        </div>
      </div>
    </Col>
  );
};

/* ─────────────────────────────────────────────
   Book Detail
───────────────────────────────────────────── */
const BookDetail = ({ book, onBack, savedBooks, onToggleSave }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const isSaved = savedBooks.has(book.id);
  const { baseUrlForFiles } = useContext(AppContext);

  const pdfUrl = book.pdf_file
    ? `${baseUrlForFiles}/ebooks/${book.pdf_file}`
    : null;

  const imgSrc =
    book.img && !imgFailed
      ? `${baseUrlForFiles}/${book.img}`
      : null;

  return (
    <div
      className="gcstudent-bb-detail-wrap gcstudent-bb-detail-anim"
      style={{ paddingTop: "100px" , paddingLeft : "80px"}}
      /* ✅ Navbar overlap fix: HeroSection used to provide top spacing.
         Since hero is hidden on this detail view now, push content down
         manually so it doesn't sit under the fixed navbar. Adjust this
         px value if the navbar height changes later. */
    >
      <button className="gcstudent-bb-back-btn" onClick={onBack}>
        ← Back to Library
      </button>

      <Row className="gcstudent-bb-detail-row">
        <Col xs={12} md={4} lg={3} className="gcstudent-bb-detail-img-col">
          {imgSrc ? (
            <img src={imgSrc} alt={book.title}
              className="gcstudent-bb-detail-cover-img"
              onError={() => {
                console.error("Ebook cover image failed to load:", imgSrc, "raw book.img:", book.img);
                setImgFailed(true);
              }} />
          ) : (
            <div className="gcstudent-bb-detail-placeholder">
              <svg width="58" height="58" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.3">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                <path d="M14 2v6h6" />
              </svg>
              <span>PDF</span>
            </div>
          )}
        </Col>

        <Col xs={12} md={8} lg={9} className="gcstudent-bb-detail-info-col">
          <span className="gcstudent-bb-detail-cat-tag">{book.dept || "General"}</span>
          <h2 className="gcstudent-bb-detail-title">{book.title}</h2>

          <div className="gcstudent-bb-meta-row">
            <div className="gcstudent-bb-meta-pill">
              Author: <strong>{book.author}</strong>
            </div>
          </div>

          <div className="gcstudent-bb-action-row">
            {pdfUrl ? (
              <a href={pdfUrl} target="_blank" rel="noreferrer"
                className="gcstudent-bb-btn-pdf">
                Download PDF
              </a>
            ) : (
              <button className="gcstudent-bb-btn-pdf" disabled>
                No PDF Available
              </button>
            )}

            <button
              className={`gcstudent-dlb-save-btn ${isSaved ? "gcstudent-dlb-save-btn--saved" : ""}`}
              onClick={() => onToggleSave(book)}
            >
              {isSaved ? "✓ Saved" : "Save for Later"}
            </button>
          </div>

          {book.Summary && (
            <div className="gcstudent-bb-ai-card">
              <div className="gcstudent-bb-ai-header">🤖 AI Summary</div>
              <p className="gcstudent-bb-ai-text">{book.Summary}</p>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
// ✅ selectedBook / setSelectedBook now come from the parent (GcDigitalLibrary)
// instead of being local state, so the parent can hide the hero section
// (HeroSection) when a book detail view is active.
const DigitalLibraryBooks = ({ selectedBook, setSelectedBook }) => {
  const [allBooks,     setAllBooks]     = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery,  setSearchQuery]  = useState("");
  const [loading,      setLoading]      = useState(false);
  // savedBooks: Map<ebook_id, favourite_record_id> — favourite_record_id is
  // needed later to call the correct removeSaved.../{id} (that id is NOT the ebook id)
  const [savedBooks,   setSavedBooks]   = useState(new Map());
  
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { baseUrl, authToken, loggedInUser, userType } = useContext(AppContext);

  /*
    Role-aware endpoint config — student and teacher each have their own
    saved-ebooks routes/fields on the backend (same Lbfavouritebook table,
    different owner column: lbstudent_id vs lbteacher_id).
  */
  const isTeacher = userType === "teacher";

  const endpoints = isTeacher
    ? {
        getSaved: (id) => `${baseUrl}/teacher/ebooks/getSavedEbooks/${id}`,
        save: `${baseUrl}/teacher/ebooks/saveEbook`,
        remove: (favId) => `${baseUrl}/teacher/ebooks/removeSavedEbook/${favId}`,
        ownerField: "lbteacher_id",
      }
   : {
    getSaved: (id) => `${baseUrl}/student/notes/getSavedEbooks/${id}`,
    save: `${baseUrl}/student/ebooks/saveEbook`,
    remove: (favId) => `${baseUrl}/student/ebooks/removeSavedEbook/${favId}`,
    ownerField: "lbstudent_id",
  };
  /* FETCH BOOKS */
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${baseUrl}/general/ebooks/all`)
      .then((res) => {
        setLoading(false);
        if (res.data.success === true)
          setAllBooks((res.data.ebooks || []).filter(Boolean));
      })
      .catch((err) => { setLoading(false); console.error(err); });
  }, [baseUrl]);

  /* FETCH ALREADY-SAVED EBOOKS (so "✓ Saved" persists across visits) */
  useEffect(() => {
    if (!loggedInUser?.id || !authToken) return;

    axios
      .get(endpoints.getSaved(loggedInUser.id), {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          const map = new Map();
          (res.data.ebooks || []).forEach((fav) => {
            if (fav.lbebook_id) map.set(fav.lbebook_id, fav.id);
          });
          setSavedBooks(map);
        }
      })
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, authToken, loggedInUser, userType]);

  /* FILTER */
  const filteredBooks = useMemo(() => {
    setVisibleCount(PAGE_SIZE); // reset on filter/search change
    return allBooks.filter((b) => {
      const matchCat    = activeCategory === "All" || b.dept === activeCategory;
      const matchSearch =
        b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.author?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [allBooks, activeCategory, searchQuery]);

  const visibleBooks = filteredBooks.slice(0, visibleCount);
  const hasMore      = visibleCount < filteredBooks.length;

  /* HANDLERS */
  const handleBookClick = (book) => {
    setSelectedBook(book);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
const handleToggleSave = (book) => {
  if (!loggedInUser?.id) {

  Swal.fire({
    icon: "warning",
    title: "Login Required",
    text: "Please login to save ebooks to your Saved PDFs.",
    confirmButtonColor: "var(--primary-color)",
  });

  return;
}

    const favouriteId = savedBooks.get(book.id);

    if (favouriteId) {
      // ── Unsave: delete the favourite record ──
      axios
        .delete(endpoints.remove(favouriteId), {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((res) => {
          if (res.data.success === true) {
            setSavedBooks((prev) => {
              const next = new Map(prev);
              next.delete(book.id);
              return next;
            });
            Swal.fire({
  icon: "success",
  title: "Removed Successfully!",
  text: `"${book.title}" has been removed from your Saved PDFs.`,
  confirmButtonColor: "var(--primary-color)",
  timer: 1800,
  showConfirmButton: false,
});
          }
        })
        .catch((err) => console.error(err));
    } else {
      // ── Save: create the favourite record ──
      axios
        .post(
          endpoints.save,
          {
            [endpoints.ownerField]: loggedInUser.id,
            lbebook_id: book.id,
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
        .then((res) => {
          if (res.data.success === true) {
            setSavedBooks((prev) => {
              const next = new Map(prev);
              next.set(book.id, res.data.saved.id);
              return next;
            });
            Swal.fire({
  icon: "success",
  title: "Saved Successfully!",
  text: "The ebook has been added to your Saved PDFs.",
  confirmButtonColor: "var(--primary-color)",
  timer: 1800,
  showConfirmButton: false,
});

} else {

  Swal.fire({
    icon: "error",
    title: "Save Failed",
    text: res.data.message || "Could not save the ebook.",
    confirmButtonColor: "var(--primary-color)",
  });

}
        })
        .catch((err) => console.error(err));
    }
  };

 return (
    <Container fluid="lg" className="gcstudent-bb-wrapper">
      {selectedBook ? (
        <BookDetail
          book={selectedBook}
          onBack={() => setSelectedBook(null)}
          savedBooks={savedBooks}
          onToggleSave={handleToggleSave}
        />
      ) : (
        <div className="gcstudent-bb-list-view gcstudent-bb-list-anim">

           {/* SEARCH BAR */}
        <div className="gcStudent-notes-search-wrap">
          <div className="gcStudent-notes-search-inner">
            <input
              type="text"
              className="gcStudent-notes-search-input"
              placeholder="Search by title, subject or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="gcStudent-notes-search-btn">Search</button>
          </div>
        </div>

          {/* FILTERS */}
          <div className="gcstudent-bb-filter-bar">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`gcstudent-bb-filter-btn ${activeCategory === cat ? "gcstudent-bb-filter-active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* GRID */}
         {loading ? (
  <Row className="gcstudent-bb-grid">
    {Array.from({ length: PAGE_SIZE }).map((_, i) => (
      <Col key={`skeleton-${i}`} xs={12} sm={6} md={4} lg={3} className="gcstudent-bb-card-col">
        <div className="gcstudent-bb-skeleton-card">
          <div className="gcstudent-bb-skeleton-img" />
          <div className="gcstudent-bb-skeleton-info">
            <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-badge" />
            <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-title" />
            <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-title-short" />
            <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-author" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
      ) : visibleBooks.length > 0 ? (
            <>
              <Row className="gcstudent-bb-grid">
                {visibleBooks.map((book, index) => (
                  <BookCard
                    key={book.id}
                    book={book}
                    index={index}
                    onClick={handleBookClick}
                  />
                ))}
              </Row>

              {/* ── Load More Button ── */}
              {hasMore && (
                <div className="gcstudent-bb-load-more-row">
                  <button
                    className="gcstudent-bb-load-more-btn"
                    onClick={() => setVisibleCount((p) => p + PAGE_SIZE)}
                  >
                    Load More Books
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12l7 7 7-7"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="gcstudent-bb-empty">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
                style={{ opacity: 0.3, marginBottom: "12px" }}>
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
              </svg>
              <p>No ebooks found.</p>
            </div>
          )}
        </div>
      )}

      
    </Container>
  );
};

export default DigitalLibraryBooks;